import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Crown, 
  X, 
  Sparkles, 
  ArrowRight, 
  Zap,
  Gift,
  Star
} from "lucide-react";

/**
 * UpgradeBanner Component
 * 
 * Persistent upsell visibility for Free tier users:
 * - Sticky top banner (dismissable for session)
 * - Always-visible sidebar upgrade card
 * 
 * Usage:
 * <UpgradeBanner userTier="free" />
 * <UpgradeSidebarCard userTier="free" />
 */

// Super Admin email - never show upgrade banners
const SUPER_ADMIN_EMAIL = "georgebroughton71@gmail.com";

// Top Sticky Banner
export const UpgradeBanner = ({ 
  userTier = "free",
  userEmail = "",
  variant = "default", // default, promo, trial
  onDismiss,
  className = ""
}) => {
  const [isDismissed, setIsDismissed] = useState(false);
  const navigate = useNavigate();
  
  // Check if previously dismissed this session
  useEffect(() => {
    const dismissed = sessionStorage.getItem("upgrade_banner_dismissed");
    if (dismissed === "true") {
      setIsDismissed(true);
    }
  }, []);
  
  const handleDismiss = () => {
    setIsDismissed(true);
    // Store dismissal in session storage (comes back on page reload)
    sessionStorage.setItem("upgrade_banner_dismissed", "true");
    onDismiss?.();
  };
  
  const handleUpgrade = () => {
    navigate("/membership?source=banner");
  };

  // Don't show for Super Admin, Pro/Elite users, or if dismissed
  const isSuperAdmin = userEmail === SUPER_ADMIN_EMAIL;
  if (isSuperAdmin || userTier !== "free" || isDismissed) {
    return null;
  }

  return (
    <div className={`sticky top-0 z-50 bg-gradient-to-r from-amber-600 via-orange-500 to-amber-600 text-black px-4 py-2.5 ${className}`}>
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        {/* Message */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="hidden sm:flex items-center justify-center w-8 h-8 bg-black/20 rounded-full flex-shrink-0">
            <Crown className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm sm:text-base font-semibold truncate">
              <span className="hidden sm:inline">🚀 Unlock Your Full Potential! </span>
              <span className="sm:hidden">🚀 Go Pro! </span>
              <span className="font-normal opacity-90 hidden md:inline">
                Get unlimited clients, pro tools, and grow your business.
              </span>
            </p>
          </div>
        </div>
        
        {/* CTA */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            onClick={handleUpgrade}
            size="sm"
            className="bg-black text-amber-400 hover:bg-black/80 font-bold px-4 h-8"
            data-testid="banner-upgrade-btn"
          >
            <Zap className="w-3 h-3 mr-1" />
            <span className="hidden sm:inline">Upgrade Now</span>
            <span className="sm:hidden">Upgrade</span>
          </Button>
          <button
            onClick={handleDismiss}
            className="p-1.5 hover:bg-black/20 rounded-full transition-colors"
            aria-label="Dismiss banner"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Sidebar Upgrade Card (Always Visible)
export const UpgradeSidebarCard = ({ 
  userTier = "free",
  userEmail = "",
  compact = false,
  className = ""
}) => {
  const navigate = useNavigate();
  
  // Don't show for Super Admin or Pro/Elite users
  const isSuperAdmin = userEmail === SUPER_ADMIN_EMAIL;
  if (isSuperAdmin || userTier !== "free") {
    return null;
  }
  
  const handleUpgrade = () => {
    navigate("/membership?source=sidebar");
  };

  if (compact) {
    return (
      <button
        onClick={handleUpgrade}
        className={`w-full flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-lg hover:border-amber-400/50 hover:from-amber-500/30 hover:to-orange-500/30 transition-all group ${className}`}
        data-testid="sidebar-upgrade-btn"
      >
        <Crown className="w-5 h-5 text-amber-400 group-hover:scale-110 transition-transform" />
        <div className="flex-1 text-left">
          <span className="text-amber-400 text-sm font-semibold">Upgrade to Pro</span>
          <p className="text-stone-500 text-xs">$29.99/mo • 30 days free</p>
        </div>
        <ArrowRight className="w-4 h-4 text-amber-400 opacity-0 group-hover:opacity-100 transition-opacity" />
      </button>
    );
  }

  return (
    <div className={`p-4 bg-gradient-to-br from-amber-500/10 via-orange-500/10 to-yellow-500/10 border border-amber-500/30 rounded-xl ${className}`}>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center">
          <Crown className="w-5 h-5 text-white" />
        </div>
        <div>
          <h4 className="font-bold text-white">Level Up!</h4>
          <p className="text-xs text-stone-400">Unlock Pro features</p>
        </div>
      </div>
      
      <ul className="space-y-2 mb-4">
        {[
          { icon: Star, text: "Unlimited clients" },
          { icon: Sparkles, text: "Pro Mini-Site" },
          { icon: Gift, text: "Loyalty rewards" }
        ].map((item, i) => (
          <li key={i} className="flex items-center gap-2 text-sm text-stone-300">
            <item.icon className="w-4 h-4 text-amber-400" />
            {item.text}
          </li>
        ))}
      </ul>
      
      <Button
        onClick={handleUpgrade}
        className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-black font-bold"
        data-testid="sidebar-upgrade-card-btn"
      >
        <Zap className="w-4 h-4 mr-2" />
        Start 30-Day Free Trial
      </Button>
      
      <p className="text-center text-xs text-stone-500 mt-2">
        Then $29.99/month
      </p>
    </div>
  );
};

// Feature Locked Redirect (for when Free user tries to access Pro/Elite feature)
export const FeatureLockedRedirect = ({ 
  featureName = "this feature",
  requiredTier = "pro"
}) => {
  const navigate = useNavigate();
  
  const handleUpgrade = () => {
    const returnUrl = encodeURIComponent(window.location.pathname);
    navigate(`/membership?feature=${featureName}&tier=${requiredTier}&return=${returnUrl}`);
  };

  return (
    <div className="min-h-[400px] flex items-center justify-center p-6">
      <div className="text-center max-w-md">
        {/* Animated Icon */}
        <div className="relative mb-6">
          <div className="w-24 h-24 bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-full flex items-center justify-center mx-auto animate-pulse">
            <Crown className="w-12 h-12 text-amber-400" />
          </div>
          <div className="absolute -top-2 -right-2 w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center animate-bounce">
            <Sparkles className="w-4 h-4 text-black" />
          </div>
        </div>
        
        {/* Content */}
        <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 mb-4">
          {requiredTier === "elite" ? "Elite" : "Pro"} Feature
        </Badge>
        
        <h2 className="text-2xl font-bold text-white mb-2">
          Unlock {featureName}
        </h2>
        
        <p className="text-stone-400 mb-6">
          Level up your business with {requiredTier === "elite" ? "Elite" : "Pro"} features. 
          Get unlimited clients, advanced tools, and more.
        </p>
        
        {/* Price */}
        <div className="bg-stone-800/50 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-center gap-2 mb-1">
            <span className="text-3xl font-bold text-white">
              {requiredTier === "elite" ? "$49.99" : "$29.99"}
            </span>
            <span className="text-stone-400">/month</span>
          </div>
          <p className="text-xs text-amber-400">
            30 days FREE trial included!
          </p>
        </div>
        
        {/* CTA */}
        <Button
          onClick={handleUpgrade}
          size="lg"
          className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-black font-bold h-12"
          data-testid="feature-locked-upgrade-btn"
        >
          <Crown className="w-5 h-5 mr-2" />
          Start 30-Day Free Trial
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
        
        <p className="text-xs text-stone-500 mt-4">
          30-day free trial • Cancel anytime
        </p>
      </div>
    </div>
  );
};

export default UpgradeBanner;
